// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/RfqMyQuotes/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xHasRoles } from "@pace/App/Store/Selectors/SessionSelectors";
import Button, { ButtonPresentationProps } from "@pace/MobiusOverrides/Button/Button";
import { sharedDateTimeOptions } from "@pace/XDL/constants";
import { parseBoolean } from "@pace/XDL/Utilities/helpers";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    quotesDataView: getQuotesDataView(state, state.pages.rfqMyQuotes.getQuotesParameter),
    rfqQuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    language: state.context.session.language,
    // XNG-Changes
    checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    cartState: getCurrentCartState(state),
    cannotAddToCart: xHasRoles(state, ["CannotAddToCart"]),
    quotesParameter: state.pages.rfqMyQuotes.getQuotesParameter,
});

// Xng-Change: PAC-1184 Quote Table Sorting
const mapDispatchToProps = {
    updateSearchFields,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// Xng-Change: PAC-1184 Quote Table Sorting
type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    expirationDate: ["expirationDate"],
    orderDate: ["orderDate"],
    status: ["status"],
};
export interface RfqMyQuotesTableStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    quoteNumberHeader?: DataTableHeaderProps;
    typeHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    orderDateHeader?: DataTableHeaderProps;
    expirationDateHeader?: DataTableHeaderProps;
    salesRepHeader?: DataTableHeaderProps;
    userHeader?: DataTableHeaderProps;
    customerHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    quoteNumberCell?: DataTableCellProps;
    quoteLink?: LinkPresentationProps;
    typeCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    orderDateCell?: DataTableCellProps;
    expirationDateCell?: DataTableCellProps;
    salesRepCell?: DataTableCellProps;
    userCell?: DataTableCellProps;
    customerCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    // XNG-Changes
    dataCell?: DataTableCellProps;
    jobCell?: DataTableCellProps;
    addToCartButton?: ButtonPresentationProps;
    cartNotificationModal?: TwoButtonModalStyles;
}

export const rfqMyQuotesTableStyles: RfqMyQuotesTableStyles = {
    wrapper: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    customerCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
    shipToCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 200px;
            `,
        },
    },
};

const enum quoteStatus {
    Complete = "Complete",
    QuoteProposed = "QuoteProposed",
}

const styles = rfqMyQuotesTableStyles;

const RfqMyQuotesTable = ({
    session,
    quotesDataView,
    rfqQuoteDetailsPageUrl,
    language,
    checkoutShippingPageUrl,
    cartState,
    // Xng-Changes
    cannotAddToCart,
    updateSearchFields,
    quotesParameter,
}: Props) => {
    const quotes = quotesDataView.value;
    if (!quotes) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (quotes.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>{translate("No quotes found")}</Typography>
            </StyledWrapper>
        );
    }
    // XNG-Changes
    const [quoteNotificationModalIsOpen, setQuoteNotificationModalIsOpen] = React.useState(false);
    const [quoteId, setQuoteId] = React.useState("");

    // Xng-Change: display Erp quote.erpOrderNumber if exist instead of Opti quoteNumber
    const rows = quotes.map(quote => ({
        id: quote.id,
        quoteNumber: quote.erpOrderNumber || quote.quoteNumber,
        type: quote.typeDisplay,
        status: quote.statusDisplay,
        orderDate: quote.orderDate
            ? getLocalizedDateTime({
                  dateTime: new Date(quote.orderDate),
                  language,
                  /**
                   * XNG-Changes:
                   * OOTB issue: Date Format fix
                   */
                  options: {
                      ...sharedDateTimeOptions,
                  },
              })
            : "",
        expirationDate: quote.expirationDate
            ? getLocalizedDateTime({
                  dateTime: new Date(quote.expirationDate),
                  language,
                  /**
                   * XNG-Changes:
                   * OOTB issue: Date Format fix
                   */
                  options: {
                      ...sharedDateTimeOptions,
                  },
              })
            : "",
        salesRep: quote.salespersonName,
        user: quote.userName,
        customer: quote.customerName,
        shipTo: quote.shipToFullAddress,
        // XNG-Changes
        poNumber: quote.poNumber,
        jobName: quote.properties.jobNumber,
        orderSubTotalDisplay: quote.orderSubTotalDisplay,
        failedToGetRealTimeInventory: quote.failedToGetRealTimeInventory,
        isSalesPerson: quote.isSalesperson,
        canAcceptQuote: parseBoolean(quote.properties.canAcceptQuote),
        account: quote.customerNumber,
    }));

    const history = useHistory();

    // XNG-Changes
    // parse isFromQuoteListPage=true
    const redirectToCheckout = (id: string) => {
        if (checkoutShippingPageUrl) {
            history.push(`${checkoutShippingPageUrl}?cartId=${id}&isFromQuoteListPage=true`);
        }
    };
    const cancelQuoteCheckoutHandler = () => {
        setQuoteNotificationModalIsOpen(false);
    };

    // XNG-Changes
    const addToCartClickHandler = (id: string) => {
        setQuoteId(id);
        if (cartState.value?.lineCount === 0) {
            redirectToCheckout(id);
        } else {
            setQuoteNotificationModalIsOpen(true);
        }
    };

    const submitQuoteCheckoutHandler = () => {
        redirectToCheckout(quoteId);
    };

    // Xng-Change: PAC-1184 Quote Table Sorting
    const headerClick = (sortField: string) => {
        const sort = quotesParameter.sort === sortField ? `${sortField} DESC` : sortField;
        quotesParameter = {
            ...quotesParameter,
            sort,
        };
        updateSearchFields({ sort });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (quotesParameter.sort === sortField) {
            sorted = "ascending";
        } else if (quotesParameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }

        return sorted as SortOrderOptions;
    };

    // Xng-Change: PAC-1184 Quote Table Sorting (status, orderDate and expirationDate)
    return (
        <StyledWrapper {...styles.wrapper}>
            <DataTable {...styles.dataTable} data-test-selector="rfqMyQuotes_quoteTable">
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader {...styles.quoteNumberHeader} title={translate("Quote Number")}>
                        {translate("Quote #")}
                    </DataTableHeader>
                    {/* Xng-Change PAC-1274 hide type column */}
                    {/* <DataTableHeader tight {...styles.typeHeader}>
                        {translate("Type")}
                    </DataTableHeader> */}
                    <DataTableHeader
                        tight
                        {...styles.statusHeader}
                        sorted={sorted("status")}
                        onSortClick={() => headerClick("status")}>
                        {translate("Status")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        {...styles.orderDateHeader}
                        sorted={sorted("orderDate")}
                        onSortClick={() => headerClick("orderDate")}>
                        {translate("Date Submitted")}
                    </DataTableHeader>
                    <DataTableHeader
                        tight
                        {...styles.expirationDateHeader}
                        sorted={sorted("expirationDate")}
                        onSortClick={() => headerClick("expirationDate")}>
                        {translate("Expiration Date")}
                    </DataTableHeader>
                    {session.isSalesPerson && (
                        <>
                            <DataTableHeader tight {...styles.salesRepHeader}>
                                {translate("Sales Rep")}
                            </DataTableHeader>
                            <DataTableHeader tight {...styles.userHeader}>
                                {translate("User")}
                            </DataTableHeader>
                        </>
                    )}
                    <DataTableHeader tight {...styles.customerHeader}>
                        {translate("Customer")}
                    </DataTableHeader>
                    {/* Xng-Change PAC-1274 hide shipTo column */}
                    {/* {!session.isSalesPerson && (
                        <DataTableHeader tight {...styles.shipToHeader} title={translate("Fulfillment")}>
                            {translate("Ship To/Pick Up")}
                        </DataTableHeader>
                    )} */}

                    <DataTableHeader tight {...styles.statusHeader}>
                        {translate("Job Name")}
                    </DataTableHeader>

                    <DataTableHeader tight {...styles.statusHeader}>
                        {translate("Account")}
                    </DataTableHeader>
                    <DataTableHeader tight {...styles.expirationDateHeader}>
                        {translate("Total Amount")}
                    </DataTableHeader>

                    <DataTableHeader tight {...styles.expirationDateHeader}>
                        {translate("Action")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {rows.map(
                        ({
                            id,
                            quoteNumber,
                            // type,
                            orderDate,
                            expirationDate,
                            salesRep,
                            user,
                            customer,
                            // shipTo,
                            orderSubTotalDisplay,
                            failedToGetRealTimeInventory,
                            isSalesPerson,
                            canAcceptQuote,
                            status,
                            // XNG-Changes
                            jobName,
                            account,
                        }) => (
                            <DataTableRow
                                key={id}
                                {...styles.dataTableRow}
                                data-test-selector={`rfqMyQuotes_quoteLine_${id}`}
                            >
                                <DataTableCell {...styles.quoteNumberCell}>
                                    <Link
                                        {...styles.quoteLink}
                                        href={`${rfqQuoteDetailsPageUrl}?quoteId=${id}`}
                                        data-test-selector="link"
                                        aria-label={quoteNumber}
                                    >
                                        {quoteNumber}
                                    </Link>
                                </DataTableCell>
                                {/* Xng-Change hide type column */}
                                {/* <DataTableCell {...styles.typeCell}>{type}</DataTableCell> */}
                                {/* Xng-Change: PAC-1274 parse status for changing colors */}
                                <DataTableCell {...styles.statusCell} { ...{status} }>{status}</DataTableCell>
                                <DataTableCell {...styles.orderDateCell}>{orderDate}</DataTableCell>
                                <DataTableCell {...styles.expirationDateCell}>{expirationDate}</DataTableCell>
                                {session.isSalesPerson && (
                                    <>
                                        <DataTableCell {...styles.salesRepCell}>{salesRep}</DataTableCell>
                                        <DataTableCell {...styles.userCell} data-test-selector="user">
                                            {user}
                                        </DataTableCell>
                                    </>
                                )}
                                <DataTableCell {...styles.customerCell}>{customer}</DataTableCell>
                                {/* Xng-Change hide shipTo column */}
                                {/* {!session.isSalesPerson && (
                                    <DataTableCell {...styles.shipToCell}>{shipTo}</DataTableCell>
                                )} */}

                                <DataTableCell {...styles.jobCell}>{jobName}</DataTableCell>
                                <DataTableCell {...styles.dataCell}>{account}</DataTableCell>
                                <DataTableCell {...styles.statusCell}>
                                    {orderSubTotalDisplay}
                                </DataTableCell>
                                <DataTableCell {...styles.statusCell}>
                                    {/* XNG-Changes: using canAcceptQuote prop */}
                                    {!failedToGetRealTimeInventory &&
                                        !isSalesPerson &&
                                        canAcceptQuote &&
                                        // Xng-Changes
                                        !cannotAddToCart && (
                                            <Button
                                                {...styles.addToCartButton}
                                                onClick={() => addToCartClickHandler(id)}
                                                data-test-selector="MyQuotes_addToCartButton"
                                                aria-label="Add To Cart"
                                            >
                                                {translate("Accept")}
                                            </Button>
                                        )}
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                    {/* XNG-Changes */}
                    <TwoButtonModal
                        {...styles.cartNotificationModal}
                        modalIsOpen={quoteNotificationModalIsOpen}
                        headlineText={translate("Cart Notification")}
                        messageText={siteMessage("Custom_Rfq_CartNotificationMessage")}
                        cancelButtonText={translate("Cancel")}
                        submitButtonText={translate("Continue")}
                        onCancel={cancelQuoteCheckoutHandler}
                        onSubmit={submitQuoteCheckoutHandler}
                        submitTestSelector="QuoteAddToCartCartNotification"
                    />
                </DataTableBody>
            </DataTable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RfqMyQuotesTable),
    definition: {
        group: "RFQ My Quotes",
        displayName: "Search Results Table",
        allowedContexts: ["RfqMyQuotesPage"],
    },
};

export default widgetModule;
